import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { jobsAPI, emailsAPI, candidatesAPI, interviewsAPI } from '../api/client';
import { toast } from 'react-toastify';
import { mayPerformWithRole } from '../utils/userAccess';
import LoadingButton from "../components/LoadingButton";
import ConfirmModal from "../components/ConfirmModal";
import EmptyState from "../components/EmptyState";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import WorkOffIcon from "@mui/icons-material/WorkOff";
import JobDashboard from './JobDashboard';
import '../pages/AnimationsAndStyles.css';

const DATE_PRESETS = [
  { key: 'overall', label: 'Overall' },
  { key: 'this_week', label: 'This Week' },
  { key: 'this_month', label: 'This Month' },
  { key: 'last_30', label: 'Last 30 Days' },
  { key: 'last_90', label: 'Last 90 Days' },
  { key: 'specific', label: 'Specific Date' },
  { key: 'custom', label: 'Custom Range' },
];

const INTERVIEW_STATUS_MAP = {
  scheduled: 'Scheduled',
  completed: 'Completed',
  cancelled: 'Cancelled',
  rescheduled: 'Rescheduled',
};

const ALL_CANDIDATE_STATUSES = [
  'applied', 'screen_pending', 'screen_selected', 'screen_rejected',
  'l1_scheduled', 'l1_select', 'l1_reject',
  'l2_scheduled', 'l2_select', 'l2_reject',
  'shared_to_client', 'hr_round',
  'col_issued', 'col_accepted', 'col_rejected',
  'fol_issued', 'fol_accepted', 'fol_rejected',
  'joined', 'candidate_backed', 'another_profile_requested',
  'cancelled', 'drop', 'hold',
];

function getDateRange(preset, specificDate, customStart, customEnd) {
  const now = new Date();
  const startOfDay = (d) => { const r = new Date(d); r.setHours(0, 0, 0, 0); return r; };
  const endOfDay = (d) => { const r = new Date(d); r.setHours(23, 59, 59, 999); return r; };

  switch (preset) {
    case 'overall':
      return null;
    case 'this_week': {
      const s = new Date(now);
      const weekday = now.getDay();
      s.setDate(now.getDate() + (weekday === 0 ? -6 : 1 - weekday));
      s.setHours(0, 0, 0, 0);
      return { start: s, end: endOfDay(now) };
    }
    case 'this_month': {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: s, end: now };
    }
    case 'last_30': {
      const s = new Date(now);
      s.setDate(now.getDate() - 30);
      s.setHours(0, 0, 0, 0);
      return { start: s, end: now };
    }
    case 'last_90': {
      const s = new Date(now);
      s.setDate(now.getDate() - 90);
      s.setHours(0, 0, 0, 0);
      return { start: s, end: now };
    }
    case 'specific': {
      if (!specificDate) return null;
      const d = new Date(specificDate);
      return { start: startOfDay(d), end: endOfDay(d) };
    }
    case 'custom': {
      if (!customStart || !customEnd) return null;
      return { start: startOfDay(new Date(customStart)), end: endOfDay(new Date(customEnd)) };
    }
    default:
      return null;
  }
}

function isDateInRange(dateStr, range) {
  if (!range) return true;
  if (!dateStr) return false;
  const d = new Date(dateStr);
  return d >= range.start && d <= range.end;
}

export default function Jobs() {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeView, setActiveView] = useState(location.state?.activeView || 'dashboard');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [isDeletingJob, setIsDeletingJob] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [isUpdatingJob, setIsUpdatingJob] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [clientNameFilter, setClientNameFilter] = useState('');
  const [clientSearchOpen, setClientSearchOpen] = useState(false);
  const [clientSearchText, setClientSearchText] = useState('');
  const clientDropdownRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [allCandidates, setAllCandidates] = useState([]);
  const [allInterviews, setAllInterviews] = useState([]);
  const [filters, setFilters] = useState({
    datePreset: 'overall',
    specificDate: '',
    customStart: '',
    customEnd: '',
    department: '',
    location: '',
    jobStatus: '',
    candidateStatus: '',
    interviewStatus: '',
  });
  const [editForm, setEditForm] = useState({
    title: "",
    client_name: "",
    experience_required: "",
    location: "",
    ctc_range: "",
    department: "",
    notice_period: "",
    skills: "",
    job_description: "",
    positions: 1,
    status: "open",
    priority: "",
    job_date: "",
    recruit_manager: "",
    hiring_manager_name: "",
    hiring_manager_email: "",
    request_by: "",
    created_by: "",
    requirement_approx_value: ""
  });
  const user = JSON.parse(localStorage.getItem('user'));
  const canManageJobs = mayPerformWithRole(user?.role, "job", "add", "job.manage");
  const canEditJobs = mayPerformWithRole(user?.role, "job", "edit", "job.edit");
  const canExportJobs = mayPerformWithRole(user?.role, "job", "export", "job.export");

  const [showWeeklyReportModal, setShowWeeklyReportModal] = useState(false);
  const [sendingWeeklyReport, setSendingWeeklyReport] = useState(false);
  const [weeklyReportData, setWeeklyReportData] = useState({ to: "", cc: "", mode: "lastWeek", startDate: "", endDate: "" });

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const [jobsRes, candRes, intRes] = await Promise.all([
        jobsAPI.getAll(),
        candidatesAPI.getAll(),
        interviewsAPI.getAll({ scope: 'global' }),
      ]);
      setJobs(jobsRes.data);
      setAllCandidates(Array.isArray(candRes.data) ? candRes.data : []);
      setAllInterviews(Array.isArray(intRes.data) ? intRes.data : []);
    } catch {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const openWeeklyReportModal = () => {
    setWeeklyReportData({ to: "", cc: "", mode: "lastWeek", startDate: "", endDate: "" });
    setShowWeeklyReportModal(true);
  };

  const sendWeeklyReportNow = async () => {
    try {
      setSendingWeeklyReport(true);

      const to = weeklyReportData.to
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
      const cc = weeklyReportData.cc
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);

      const payload = { mode: weeklyReportData.mode };
      if (to.length) payload.to = to;
      if (cc.length) payload.cc = cc;
      if (weeklyReportData.mode === "custom") {
        payload.startDate = weeklyReportData.startDate;
        payload.endDate = weeklyReportData.endDate;
      }

      const res = await emailsAPI.sendWeeklyReport(payload);
      const toCount = res?.data?.details?.recipients?.to?.length || 0;
      toast.success(
        toCount
          ? `Weekly report sent to ${toCount} recipient(s)`
          : "Weekly report sent successfully"
      );
      setShowWeeklyReportModal(false);
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Failed to send weekly report";
      toast.error(message);
    } finally {
      setSendingWeeklyReport(false);
    }
  };

  const openDeleteModal = (e, id) => {
    e.stopPropagation();
    setJobToDelete(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    if (isDeletingJob) return;
    setJobToDelete(null);
    setShowDeleteModal(false);
  };

  const handleDelete = async () => {
    if (!jobToDelete || isDeletingJob) return;
    try {
      setIsDeletingJob(true);
      await jobsAPI.delete(jobToDelete);
      toast.success('Job removed');
      closeDeleteModal();
      await fetchJobs();
    } catch (err) {
      console.error('Failed to delete job:', err);
      toast.error('Failed to delete job');
    } finally {
      setIsDeletingJob(false);
    }
  };

  const openEditJob = (e, job) => {
    e.stopPropagation();
    setEditingJob(job);
    setEditForm({
      title: job.title || "",
      client_name: job.client_name || "",
      experience_required: job.experience_required || "",
      location: job.location || "",
      ctc_range: job.ctc_range || "",
      department: job.department || "",
      notice_period: job.notice_period || "",
      skills: job.skills || "",
      job_description: job.job_description || "",
      positions: job.positions ?? 1,
      status: job.status || "open",
      priority: job.priority || "",
      job_date: job.job_date || "",
      recruit_manager: job.recruit_manager || "",
      hiring_manager_name: job.hiring_manager_name || "",
      hiring_manager_email: job.hiring_manager_email || "",
      request_by: job.request_by || "",
      created_by: job.created_by || "",
      requirement_approx_value: job.requirement_approx_value || ""
    });
  };

  useEffect(() => {
    if (!loading && location.state?.editJobId && jobs.length) {
      const job = jobs.find((item) => Number(item.id) === Number(location.state.editJobId));
      if (job) openEditJob({ stopPropagation() {} }, job);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, location.state?.editJobId, jobs.length]);

  const closeEditJob = () => {
    if (isUpdatingJob) return;
    setEditingJob(null);
  };

  const saveEditedJob = async () => {
    if (!editingJob?.id) return;
    if (!String(editForm.title || "").trim()) {
      toast.error("Job title is required");
      return;
    }
    if (!editForm.positions || editForm.positions < 1) {
      toast.error("Number of positions is required (minimum 1)");
      return;
    }

    try {
      setIsUpdatingJob(true);
      await jobsAPI.update(editingJob.id, editForm);
      toast.success("Job updated successfully");
      setEditingJob(null);
      await fetchJobs();
      if (location.state?.screeningCandidateId) {
        navigate(`/candidates/${location.state.screeningCandidateId}`, {
          state: { autoSendScreening: true }
        });
      }
    } catch (err) {
      const message =
        err?.response?.data?.error || "Failed to update job";
      toast.error(message);
    } finally {
      setIsUpdatingJob(false);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'open': return 'status-open';
      case 'closed': return 'status-closed';
      case 'hold': return 'status-hold';
      case 'filled': return 'status-filled';
      default: return 'status-open';
    }
  };

  const statusLabel = (status) => {
    switch (status) {
      case 'open': return 'Open';
      case 'closed': return 'Closed';
      case 'hold': return 'On Hold';
      case 'filled': return 'Filled';
      default: return 'Open';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric'
      });
    } catch {
      return '';
    }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    try {
      const now = new Date();
      const date = new Date(dateStr);
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays < 1) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 30) return `${diffDays} days ago`;
      const diffMonths = Math.floor(diffDays / 30);
      if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
      const diffYears = Math.floor(diffMonths / 12);
      return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
    } catch {
      return '';
    }
  };

  const activeJobs = jobs.filter(j => j.status === 'open');

  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      datePreset: 'overall',
      specificDate: '',
      customStart: '',
      customEnd: '',
      department: '',
      location: '',
      jobStatus: '',
      candidateStatus: '',
      interviewStatus: '',
    });
    setClientNameFilter('');
    setClientSearchText('');
    setClientSearchOpen(false);
    setCurrentPage(1);
  }, []);

  const hasActiveFilters = filters.datePreset !== 'overall' ||
    filters.specificDate || filters.customStart || filters.customEnd ||
    filters.department || filters.location || filters.jobStatus || clientNameFilter;

  const dateRange = useMemo(
    () => getDateRange(filters.datePreset, filters.specificDate, filters.customStart, filters.customEnd),
    [filters.datePreset, filters.specificDate, filters.customStart, filters.customEnd]
  );

  const uniqueDepartments = useMemo(() => {
    const set = new Set();
    (jobs || []).forEach(j => { if (j.department) set.add(j.department); });
    return [...set].sort();
  }, [jobs]);

  const uniqueLocations = useMemo(() => {
    const set = new Set();
    (jobs || []).forEach(j => { if (j.location) set.add(j.location); });
    return [...set].sort();
  }, [jobs]);

  const uniqueJobStatuses = useMemo(() => {
    const set = new Set();
    (jobs || []).forEach(j => { if (j.status) set.add(j.status); });
    return [...set].sort();
  }, [jobs]);

  const uniqueClients = useMemo(() => {
    const set = new Set();
    (jobs || []).forEach(j => {
      const c = String(j.client_name || '').trim();
      if (c) set.add(c);
    });
    return [...set].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [jobs]);

  const filteredClientOptions = useMemo(() => {
    const q = clientSearchText.trim().toLowerCase();
    if (!q) return uniqueClients;
    return uniqueClients.filter(c => c.toLowerCase().includes(q));
  }, [uniqueClients, clientSearchText]);

  const selectClient = useCallback((name) => {
    setClientNameFilter(name);
    setClientSearchText('');
    setClientSearchOpen(false);
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    if (!clientSearchOpen) return;
    const handler = (e) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(e.target)) {
        setClientSearchOpen(false);
        setClientSearchText('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [clientSearchOpen]);

  const uniqueCandidateStatuses = useMemo(() => {
    const set = new Set();
    (allCandidates || []).forEach(c => { if (c.status) set.add(c.status); });
    return [...set].sort();
  }, [allCandidates]);

  const filteredJobs = useMemo(() => {
    let result = jobs || [];

    if (filters.department) result = result.filter(j => j.department === filters.department);
    if (filters.location) result = result.filter(j => j.location === filters.location);
    if (filters.jobStatus) result = result.filter(j => j.status === filters.jobStatus);
    if (dateRange) result = result.filter(j => isDateInRange(j.created_at, dateRange));

    if (filters.candidateStatus || filters.interviewStatus) {
      const matchingCandidateIds = new Set();
      let filteredCands = allCandidates || [];
      if (filters.candidateStatus) filteredCands = filteredCands.filter(c => c.status === filters.candidateStatus);
      if (filters.interviewStatus) {
        const intCandidateIds = new Set(
          (allInterviews || [])
            .filter(i => i.status === filters.interviewStatus)
            .map(i => i.candidate_id)
        );
        filteredCands = filteredCands.filter(c => intCandidateIds.has(c.id));
      }
      filteredCands.forEach(c => { if (c.job_id) matchingCandidateIds.add(c.job_id); });
      result = result.filter(j => matchingCandidateIds.has(j.id));
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(j =>
        j.title && j.title.toLowerCase().includes(q)
      );
    }

    if (clientNameFilter) {
      result = result.filter(j => j.client_name === clientNameFilter);
    }

    return result;
  }, [jobs, allCandidates, allInterviews, filters, dateRange, searchQuery, clientNameFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedJobs = filteredJobs.slice((safePage - 1) * pageSize, safePage * pageSize);

  const goToPage = (p) => {
    setCurrentPage(Math.max(1, Math.min(p, totalPages)));
  };

  return (
    <div style={page}>
      <style>{css}</style>

      <div className="ats-header">
        <div>
          <h2 className="ats-heading">Job Openings</h2>
          <p className="ats-subtitle">Opening-level pipeline &amp; fulfillment tracking</p>
        </div>
        <div className="ats-header-right">
          {canExportJobs && (
            <button onClick={openWeeklyReportModal} className="weekly-report-btn">
              Send Weekly Report
            </button>
          )}
          {canManageJobs && (
            <button onClick={() => navigate("/jobs/create")} className="ats-primary-btn">
              + New Opening
            </button>
          )}
        </div>
      </div>

      {/* TAB NAVIGATION */}
      <div style={tabStyles.tabContainer}>
        <button
          style={{
            ...tabStyles.tab,
            ...(activeView === 'dashboard' ? tabStyles.tabActive : {})
          }}
          onClick={() => setActiveView('dashboard')}
        >
          Job Dashboard
        </button>
        <button
          style={{
            ...tabStyles.tab,
            ...(activeView === 'list' ? tabStyles.tabActive : {})
          }}
          onClick={() => setActiveView('list')}
        >
          Job List
        </button>
      </div>

      {/* DASHBOARD TAB */}
      {activeView === 'dashboard' && <JobDashboard />}

           <div className="ats-summary">{filteredJobs.length} of {jobs.length} Total Openings{activeJobs.length > 0 ? ` \u2022 ${activeJobs.length} Active` : ''}</div>
      {/* LIST TAB */}
      {activeView === 'list' && (<>
      <div className="jdb-filter-bar">
        <div className="jdb-filter-row">
          <div className="jdb-filter-group">
            <label className="jdb-filter-label">Date Range</label>
            <select
              className="jdb-select"
              value={filters.datePreset}
              onChange={e => updateFilter('datePreset', e.target.value)}
            >
              {DATE_PRESETS.map(p => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </div>

          {filters.datePreset === 'specific' && (
            <div className="jdb-filter-group">
              <label className="jdb-filter-label">Select Date</label>
              <input
                type="date"
                className="jdb-input"
                value={filters.specificDate}
                onChange={e => updateFilter('specificDate', e.target.value)}
              />
            </div>
          )}

          {filters.datePreset === 'custom' && (
            <div className="jdb-filter-group jdb-date-range-pair">
              <div>
                <label className="jdb-filter-label">From</label>
                <input
                  type="date"
                  className="jdb-input"
                  value={filters.customStart}
                  onChange={e => updateFilter('customStart', e.target.value)}
                />
              </div>
              <div>
                <label className="jdb-filter-label">To</label>
                <input
                  type="date"
                  className="jdb-input"
                  value={filters.customEnd}
                  onChange={e => updateFilter('customEnd', e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="jdb-filter-group">
            <label className="jdb-filter-label">Department</label>
            <select
              className="jdb-select"
              value={filters.department}
              onChange={e => updateFilter('department', e.target.value)}
            >
              <option value="">All Departments</option>
              {uniqueDepartments.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          <div className="jdb-filter-group">
            <label className="jdb-filter-label">Location</label>
            <select
              className="jdb-select"
              value={filters.location}
              onChange={e => updateFilter('location', e.target.value)}
            >
              <option value="">All Locations</option>
              {uniqueLocations.map(l => (
                <option key={l} value={l}>{l}</option>
              ))}
            </select>
          </div>

          <div className="jdb-filter-group">
            <label className="jdb-filter-label">Job Status</label>
            <select
              className="jdb-select"
              value={filters.jobStatus}
              onChange={e => updateFilter('jobStatus', e.target.value)}
            >
              <option value="">All Job Status</option>
              {uniqueJobStatuses.map(s => (
                <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
              ))}
            </select>
          </div>

          <div className="jdb-filter-group">
            <label className="jdb-filter-label">Client Name</label>
            <div className="jdb-client-dropdown" ref={clientDropdownRef}>
              <button
                type="button"
                className="jdb-select jdb-client-dropdown-btn"
                onClick={() => {
                  setClientSearchOpen(o => !o);
                  setClientSearchText('');
                }}
              >
                <span className="jdb-client-label">{clientNameFilter || 'All Clients'}</span>
                <svg className="jdb-client-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {clientSearchOpen && (
                <div className="jdb-client-menu">
                  <div className="jdb-search-wrap jdb-client-search">
                    <svg className="jdb-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    <input
                      className="jdb-input jdb-search-input"
                      type="text"
                      placeholder="Type to filter clients..."
                      value={clientSearchText}
                      onChange={(e) => setClientSearchText(e.target.value)}
                      autoFocus
                    />
                    {clientSearchText && (
                      <button className="jdb-search-clear" type="button" onClick={() => setClientSearchText('')}>&#x2715;</button>
                    )}
                  </div>
                  <div className="jdb-client-options">
                    <div
                      className={`jdb-client-option ${!clientNameFilter ? 'selected' : ''}`}
                      onClick={() => selectClient('')}
                    >
                      All Clients
                    </div>
                    {filteredClientOptions.map(name => (
                      <div
                        key={name}
                        className={`jdb-client-option ${clientNameFilter === name ? 'selected' : ''}`}
                        onClick={() => selectClient(name)}
                      >
                        {name}
                      </div>
                    ))}
                    {filteredClientOptions.length === 0 && (
                      <div className="jdb-client-empty">No matching clients</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="jdb-filter-group">
            <label className="jdb-filter-label">Search</label>
            <div className="jdb-search-wrap">
              <svg className="jdb-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input
                className="jdb-input jdb-search-input"
                type="text"
                placeholder="  Search by title..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              />
              {searchQuery && (
                <button className="jdb-search-clear" onClick={() => { setSearchQuery(''); setCurrentPage(1); }}>&#x2715;</button>
              )}
            </div>
          </div>

          {hasActiveFilters && (
            <button type="button" className="jdb-reset-btn" onClick={resetFilters}>
              Reset Filters
            </button>
          )}
        </div>
      </div>

     

      <div className="ats-table-wrap">
        {loading ? (
          <div className="ats-loading">Loading job openings...</div>
        ) : !loading && filteredJobs.length === 0 ? (
          <EmptyState
            icon={
              hasActiveFilters || searchQuery.trim()
                ? <SearchOffIcon style={{ fontSize: 48, color: "#94A3B8" }} />
                : <WorkOffIcon style={{ fontSize: 48, color: "#94A3B8" }} />
            }
            title={hasActiveFilters || searchQuery.trim() ? "No jobs match" : "No job openings yet"}
            message={
              hasActiveFilters || searchQuery.trim()
                ? "No job openings found for the selected filters. Try changing the filters or return to all openings."
                : "No job openings found. Create your first job opening!"
            }
            actionLabel={hasActiveFilters || searchQuery.trim() ? "Clear All Filters" : undefined}
            onAction={hasActiveFilters || searchQuery.trim() ? () => { resetFilters(); setSearchQuery(''); } : undefined}
          />
        ) : (
          <table className="ats-table">
            <thead>
              <tr>
                <th className="th-opening">Opening</th>
                <th className="th-department">Department</th>
                <th className="th-created">Created On</th>
                <th className="th-jobdate">Job Date</th>
                <th className="th-metrics">Metrics</th>
                <th className="th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedJobs.map((job, idx) => {
                const remaining = Math.max(0, (job.positions ?? 1) - (job.filled_positions ?? 0));
                return (
                  <tr
                    key={job.id}
                    className="ats-row"
                    onClick={() => navigate(`/jobs/${job.id}`, { state: { activeView } })}
                    style={{ animationDelay: `${idx * 0.03}s` }}
                  >
                    <td className="td-opening">
                      <div className="opening-title-wrap">
                        <span className="opening-title" title={job.title}>{job.title}</span>
                        <span className={`ats-badge ${getStatusClass(job.status)}`}>{statusLabel(job.status)}</span>
                      </div>
                      {job.client_name && <div className="opening-client">{job.client_name}</div>}
                    </td>
                    <td className="td-department">
                      {job.department && <span className="dept-badge">{job.department}</span>}
                    </td>
                    <td className="td-created">
                      {job.created_at && (
                        <>
                          <div className="date-main">{formatDate(job.created_at)}</div>
                          <div className="date-relative">{timeAgo(job.created_at)}</div>
                        </>
                      )}
                    </td>
                    <td className="td-jobdate">
                      {job.job_date && (
                        <>
                          <div className="date-main">{formatDate(job.job_date)}</div>
                          <div className="date-relative">{timeAgo(job.job_date)}</div>
                        </>
                      )}
                    </td>
                    <td className="td-metrics">
                      <div className="metrics-row">
                        <div className="metric-item">
                          <span className="metric-val">{job.positions ?? 1}</span>
                          <span className="metric-lbl">Positions</span>
                        </div>
                        <div className="metric-div" />
                        <div className="metric-item">
                          <span className="metric-val">{job.total_candidates ?? 0}</span>
                          <span className="metric-lbl">Submitted</span>
                        </div>
                        <div className="metric-div" />
                        <div className="metric-item">
                          <span className="metric-val" style={{ color: '#10B981' }}>{job.joined_count ?? 0}</span>
                          <span className="metric-lbl">Joined</span>
                        </div>
                        <div className="metric-div" />
                        <div className="metric-item">
                          <span className={`metric-val ${remaining > 0 ? 'metric-remaining' : 'metric-remaining-done'}`}>{remaining}</span>
                          <span className="metric-lbl">Remaining</span>
                        </div>
                      </div>
                    </td>
                    <td className="td-actions" onClick={(e) => e.stopPropagation()}>
                      <button className="act-btn" onClick={() => navigate(`/jobs/${job.id}`, { state: { activeView } })}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        View
                      </button>
                      {canEditJobs && (
                        <button className="act-btn act-edit" onClick={(e) => openEditJob(e, job)}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          Edit
                        </button>
                      )}
                      {canEditJobs && (
                        <button
                          className="act-btn act-delete"
                          onClick={(e) => openDeleteModal(e, job.id)}
                          disabled={isDeletingJob && jobToDelete === job.id}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                          {isDeletingJob && jobToDelete === job.id ? 'Deleting' : 'Delete'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {!loading && filteredJobs.length > 0 && totalPages > 1 && (
        <div className="ats-pagination">
          <button className="page-btn" disabled={safePage <= 1} onClick={() => goToPage(safePage - 1)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
            Prev
          </button>
          <div className="page-numbers">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
              <button key={p} className={`page-num ${p === safePage ? 'page-num-active' : ''}`} onClick={() => goToPage(p)}>{p}</button>
            ))}
          </div>
          <button className="page-btn" disabled={safePage >= totalPages} onClick={() => goToPage(safePage + 1)}>
            Next
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <span className="page-info">{filteredJobs.length} openings</span>
        </div>
      )}

      {editingJob && (
        <div className="job-edit-overlay" onClick={closeEditJob}>
          <div className="job-edit-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Job Position</h3>

            <label>Title</label>
            <input
              value={editForm.title}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              placeholder="Job title"
            />

            <label>Client Name</label>
            <input
              value={editForm.client_name}
              onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })}
              placeholder="Client name"
            />

            <div className="form-row" style={{display: 'flex', gap: '12px'}}>
              <div style={{flex: 1}}>
                <label>Positions</label>
                <input
                  type="number"
                  min="1"
                  value={editForm.positions}
                  onChange={(e) => {
                    const val = e.target.value;
                    setEditForm({ ...editForm, positions: val === "" ? "" : parseInt(val, 10) });
                  }}
                  onBlur={() => {
                    if (!editForm.positions || editForm.positions < 1) {
                      setEditForm({ ...editForm, positions: 1 });
                    }
                  }}
                  className="no-spinner"
                />
              </div>
              <div style={{flex: 1}}>
                <label>Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                >
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                  <option value="hold">On Hold</option>
                  <option value="filled">Filled</option>
                </select>
              </div>
              <div style={{flex: 1}}>
                <label>Job Date</label>
                <input
                  type="date"
                  value={editForm.job_date}
                  onChange={(e) => setEditForm({ ...editForm, job_date: e.target.value })}
                />
              </div>
            </div>

            <label>Department</label>
            <select
              value={editForm.department}
              onChange={(e) => setEditForm({ ...editForm, department: e.target.value })}
            >
              <option value="">Select Department</option>
              <option value="ICDS">ICDS</option>
              <option value="Embedded">Embedded</option>
            </select>

            <label>Priority</label>
            <select
              value={editForm.priority}
              onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
            >
              <option value="">Select Priority</option>
              <option value="Priority 1">Priority 1</option>
              <option value="Priority 2">Priority 2</option>
              <option value="Priority 3">Priority 3</option>
            </select>

            <label>Experience Required</label>
            <input
              value={editForm.experience_required}
              onChange={(e) => setEditForm({ ...editForm, experience_required: e.target.value })}
              placeholder="e.g. 3-5 years"
            />

            <label>Recruitor</label>
            <input
              value={editForm.recruit_manager}
              onChange={(e) => setEditForm({ ...editForm, recruit_manager: e.target.value })}
              placeholder="Recruit manager (optional)"
            />

            <label>Hiring Manager Name</label>
            <input
              value={editForm.hiring_manager_name}
              onChange={(e) => setEditForm({ ...editForm, hiring_manager_name: e.target.value })}
              placeholder="Hiring manager name"
            />

            <label>Hiring Manager Email</label>
            <input
              type="email"
              value={editForm.hiring_manager_email}
              onChange={(e) => setEditForm({ ...editForm, hiring_manager_email: e.target.value })}
              placeholder="manager@company.com"
            />

            <label>Request By</label>
            <input
              value={editForm.request_by}
              onChange={(e) => setEditForm({ ...editForm, request_by: e.target.value })}
              placeholder="Who requested the requirement"
            />

            <label>Created By</label>
            <input
              value={editForm.created_by}
              onChange={(e) => setEditForm({ ...editForm, created_by: e.target.value })}
              placeholder="Who created this job"
            />

            <label>Requirement Approx Value</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={editForm.requirement_approx_value}
              onChange={(e) => setEditForm({ ...editForm, requirement_approx_value: e.target.value })}
              placeholder="e.g. 2500000"
              className="no-spinner"
            />

            <label>Location</label>
            <input
              value={editForm.location}
              onChange={(e) => setEditForm({ ...editForm, location: e.target.value })}
              placeholder="Location"
            />

            <label>CTC Range</label>
            <input
              value={editForm.ctc_range}
              onChange={(e) => setEditForm({ ...editForm, ctc_range: e.target.value })}
              placeholder="e.g. 10-15 LPA"
            />

            <label>Notice Period</label>
            <input
              value={editForm.notice_period}
              onChange={(e) => setEditForm({ ...editForm, notice_period: e.target.value })}
              placeholder="e.g. Immediate / 30 days"
            />

            <label>Skills</label>
            <input
              value={editForm.skills}
              onChange={(e) => setEditForm({ ...editForm, skills: e.target.value })}
              placeholder="Comma-separated skills"
            />

            <label>Job Description</label>
            <textarea
              rows="4"
              value={editForm.job_description}
              onChange={(e) => setEditForm({ ...editForm, job_description: e.target.value })}
              placeholder="Job description"
            />

            <div className="job-edit-actions">
              <LoadingButton
                type="button"
                className="save-job-btn"
                onClick={saveEditedJob}
                loading={isUpdatingJob}
                loadingText="Saving..."
              >
                Save
              </LoadingButton>
              <button
                type="button"
                className="cancel-job-btn"
                onClick={closeEditJob}
                disabled={isUpdatingJob}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      </>)}

      {showWeeklyReportModal && (
        <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', maxWidth: '520px', width: '100%', boxShadow: '0 24px 60px rgba(15,23,42,0.28)' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '20px', fontWeight: 700 }}>Send Weekly HR Report</h3>

            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748B', marginBottom: '4px' }}>Date Range</label>
            <select
              value={weeklyReportData.mode}
              onChange={(e) => setWeeklyReportData({ ...weeklyReportData, mode: e.target.value })}
              style={{ width: '100%', padding: '10px 14px', border: '2px solid #E2E8F0', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '12px', background: '#fff' }}
            >
              <option value="lastWeek">Last Week (Mon–Sun)</option>
              <option value="thisWeek">This Week (Mon–Today)</option>
              <option value="today">Today</option>
              <option value="custom">Custom Range</option>
            </select>

            {weeklyReportData.mode === "custom" && (
              <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748B', marginBottom: '4px' }}>Start Date</label>
                  <input
                    type="date"
                    value={weeklyReportData.startDate}
                    onChange={(e) => setWeeklyReportData({ ...weeklyReportData, startDate: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', border: '2px solid #E2E8F0', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748B', marginBottom: '4px' }}>End Date</label>
                  <input
                    type="date"
                    value={weeklyReportData.endDate}
                    onChange={(e) => setWeeklyReportData({ ...weeklyReportData, endDate: e.target.value })}
                    style={{ width: '100%', padding: '10px 14px', border: '2px solid #E2E8F0', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </div>
              </div>
            )}

            <p style={{ color: '#64748B', fontSize: '13px', marginBottom: '12px' }}>
              Enter comma-separated emails. Leave blank to use backend default recipients.
            </p>

            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748B', marginBottom: '4px' }}>To</label>
            <input
              placeholder="leader1@company.com, leader2@company.com"
              value={weeklyReportData.to}
              onChange={(e) => setWeeklyReportData({ ...weeklyReportData, to: e.target.value })}
              style={{ width: '100%', padding: '10px 14px', border: '2px solid #E2E8F0', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '12px' }}
            />

            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748B', marginBottom: '4px' }}>CC</label>
            <input
              placeholder="hrhead@company.com"
              value={weeklyReportData.cc}
              onChange={(e) => setWeeklyReportData({ ...weeklyReportData, cc: e.target.value })}
              style={{ width: '100%', padding: '10px 14px', border: '2px solid #E2E8F0', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '20px' }}
            />

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <LoadingButton
                onClick={sendWeeklyReportNow}
                loading={sendingWeeklyReport}
                loadingText="Sending..."
                style={{ padding: '10px 20px', background: 'linear-gradient(135deg, #5B9BD5, #4A85BD)', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
              >
                Send Report
              </LoadingButton>
              <button
                onClick={() => setShowWeeklyReportModal(false)}
                disabled={sendingWeeklyReport}
                style={{ padding: '10px 20px', background: 'transparent', color: '#5B9BD5', border: '2px solid #5B9BD5', borderRadius: '10px', fontWeight: 600, fontSize: '13px', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={showDeleteModal}
        title="Are you sure?"
        message="Do you really want to delete this job opening? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        onCancel={closeDeleteModal}
        isLoading={isDeletingJob}
        loadingText="Deleting..."
      />
    </div>
  );
}

const page = {
  minHeight: 'auto',
  background: 'transparent',
  padding: '4px 0',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  fontSize: '14px',
  color: '#0F172A',
  animation: 'fadeInUp 0.4s ease-out',
};

const tabStyles = {
  tabContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    borderBottom: '2px solid #E5E7EB',
  },
  tab: {
    flex: 1,
    padding: '10px 18px',
    background: '#FFFFFF',
    border: '1px solid #D8E1EF',
    borderBottom: '3px solid #D8E1EF',
    borderRadius: '10px 10px 0 0',
    fontSize: '13px',
    fontWeight: '600',
    fontFamily: "var(--font-sans)",
    color: '#1E293B',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 6px 14px rgba(15, 23, 42, 0.06)',
  },
  tabActive: {
    color: '#FFFFFF',
    background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)',
    borderColor: '#0F172A',
    boxShadow: '0 10px 22px rgba(15, 23, 42, 0.18)',
  },
};

const css = `
input[type="number"].no-spinner::-webkit-inner-spin-button,
input[type="number"].no-spinner::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type="number"].no-spinner {
  -moz-appearance: textfield;
}
/* ================= HEADER ================= */
.ats-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
}

.ats-heading {
  font-size: 22px;
  font-weight: 700;
  margin: 0;
  color: #0F172A;
  font-family: 'Inter', sans-serif;
}

.ats-subtitle {
  font-size: 13px;
  color: #64748B;
  margin: 2px 0 0 0;
  font-weight: 400;
}

.ats-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.weekly-report-btn {
  padding: 7px 16px;
  background: #059669;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  transition: background 0.15s;
  white-space: nowrap;
  font-family: 'Inter', sans-serif;
}
.weekly-report-btn:hover {
  background: #047857;
}

.ats-primary-btn {
  padding: 7px 16px;
  background: #5B9BD5;
  color: #fff;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  transition: background 0.15s;
  white-space: nowrap;
  font-family: 'Inter', sans-serif;
}

.ats-primary-btn:hover {
  background: #4A85BD;
}

/* ================= SUMMARY ================= */
.ats-summary {
  font-size: 13px;
  color: #475569;
  font-weight: 500;
  white-space: nowrap;
  margin-bottom: 10px;
  padding-top: 4px;
}

/* ================= TABLE CONTAINER ================= */
.ats-table-wrap {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #E8EDF4;
  overflow-x: auto;
}

.ats-loading {
  padding: 36px;
  text-align: center;
  color: #94A3B8;
  font-size: 13px;
}

/* ================= TABLE ================= */
.ats-table {
  width: 100%;
  border-collapse: collapse;
}

.ats-table thead th {
  padding: 10px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 600;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  background: #F8FAFC;
  border-bottom: 1px solid #E8EDF4;
  white-space: nowrap;
}

.ats-table tbody tr {
  cursor: pointer;
  transition: background 0.12s;
}

.ats-table tbody tr:hover {
  background: #F8FAFC;
}

.ats-table tbody tr:active {
  background: #F1F5F9;
}

.ats-table td {
  padding: 12px 16px;
  border-bottom: 1px solid #F1F5F9;
  vertical-align: middle;
}

.ats-table tbody tr:last-child td {
  border-bottom: none;
}

/* Column sizing — auto-fit by content */
.th-created, .td-created { white-space: nowrap; }
.th-jobdate, .td-jobdate { white-space: nowrap; }
.th-metrics, .td-metrics { white-space: nowrap; }
.th-actions, .td-actions { white-space: nowrap; }
.th-department, .td-department { white-space: nowrap; }

/* ================= FILTER BAR ================= */
.jdb-filter-bar {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #E8EDF4;
  padding: 14px 18px;
  margin-bottom: 16px;
}
.jdb-filter-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  flex-wrap: wrap;
}
.jdb-filter-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 0 1 auto;
}
.jdb-filter-label {
  font-size: 10px;
  font-weight: 600;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.jdb-select {
  padding: 6px 8px;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  color: #0F172A;
  background: #fff;
  min-width: 120px;
  height: 32px;
  box-sizing: border-box;
}
.jdb-input {
  padding: 6px 8px;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  color: #0F172A;
  background: #fff;
  height: 32px;
  box-sizing: border-box;
}
.jdb-select:focus,
.jdb-input:focus {
  outline: none;
  border-color: #5B9BD5;
  box-shadow: 0 0 0 2px #rgba(91, 155, 213, 0.1);
}
.jdb-date-range-pair {
  flex-direction: row;
  gap: 4px;
}
.jdb-date-range-pair > div {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.jdb-date-range-pair input[type="date"] {
  width: 138px;
}
.jdb-filter-group > input[type="date"] {
  width: 152px;
}
.jdb-reset-btn {
  padding: 6px 12px;
  border: 1px solid #DC2626;
  border-radius: 8px;
  background: #FEF2F2;
  color: #DC2626;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
  align-self: flex-end;
  height: 32px;
  box-sizing: border-box;
}
.jdb-reset-btn:hover {
  background: #FEE2E2;
}
.jdb-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
}
.jdb-search-icon {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #94A3B8;
  pointer-events: none;
  flex-shrink: 0;
}
.jdb-search-input {
  padding-left: 28px;
  padding-right: 24px;
  width: 145px;
}
/* AnimationsAndStyles.css sets input[type="text"] padding at (0,1,1), which
   outranks .jdb-search-input (0,1,0) and pulled the cursor/placeholder under
   the leading icon. Scope under the wrapper so it wins without !important. */
.jdb-search-wrap .jdb-search-input {
  padding-left: 28px;
}
.jdb-search-clear {
  position: absolute;
  right: 6px;
  background: none;
  border: none;
  color: #94A3B8;
  cursor: pointer;
  padding: 2px 4px;
  font-size: 11px;
  line-height: 1;
}
.jdb-search-clear:hover {
  color: #475569;
}

/* ================= CLIENT NAME SEARCHABLE DROPDOWN ================= */
.jdb-client-dropdown {
  position: relative;
}
.jdb-client-dropdown-btn {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 180px;
  cursor: pointer;
  text-align: left;
}
.jdb-client-label {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #0F172A;
}
.jdb-client-chevron {
  color: #94A3B8;
  flex-shrink: 0;
}
.jdb-client-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 60;
  width: 240px;
  background: #fff;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  overflow: hidden;
}
.jdb-client-search {
  border-bottom: 1px solid #E2E8F0;
}
.jdb-client-search input.jdb-input {
  width: 100%;
  border: none;
  border-radius: 0;
  height: 34px;
}
.jdb-client-options {
  max-height: 220px;
  overflow-y: auto;
}
.jdb-client-option {
  padding: 7px 12px;
  font-size: 12px;
  color: #0F172A;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.jdb-client-option:hover {
  background: #F1F5F9;
}
.jdb-client-option.selected {
  background: #EFF6FF;
  color: #5B9BD5;
  font-weight: 600;
}
.jdb-client-empty {
  padding: 10px 12px;
  font-size: 12px;
  color: #94A3B8;
}

/* ================= DEPARTMENT COLUMN ================= */
.dept-badge {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 10px;
  background: #EFF6FF;
  color: #5B9BD5;
  white-space: nowrap;
  letter-spacing: 0.2px;
}

/* ================= OPENING COLUMN ================= */
.opening-title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 2px;
}

.opening-title {
  font-size: 14px;
  font-weight: 600;
  color: #0F172A;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
}

.opening-client {
  font-size: 14px;
  font-weight: 600;
  color: #0F172A;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 280px;
}

/* ================= STATUS BADGE ================= */
.ats-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  white-space: nowrap;
  flex-shrink: 0;
}
.status-open { background: #DCFCE7; color: #16A34A; }
.status-filled { background: #DBEAFE; color: #5B9BD5; }
.status-closed { background: #FEE2E2; color: #DC2626; }
.status-hold { background: #FEF9C3; color: #CA8A04; }

/* ================= CREATED ON COLUMN ================= */
.date-main {
  font-size: 13px;
  color: #0F172A;
  font-weight: 500;
  line-height: 1.3;
}

.date-relative {
  font-size: 11px;
  color: #94A3B8;
  margin-top: 1px;
}

/* ================= METRICS COLUMN ================= */
.metrics-row {
  display: flex;
  align-items: center;
  gap: 0;
}

.metric-item {
  flex: 1;
  text-align: center;
  min-width: 0;
}

.metric-val {
  display: block;
  font-size: 15px;
  font-weight: 700;
  color: #5B9BD5;
  line-height: 1.2;
  font-family: 'Inter', sans-serif;
}

.metric-lbl {
  display: block;
  font-size: 10px;
  color: #64748B;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 1px;
}

.metric-div {
  width: 1px;
  height: 32px;
  background: #E8EDF4;
  flex-shrink: 0;
}

.metric-remaining {
  color: #C2410C;
  font-size: 17px;
}

.metric-remaining-done {
  color: #16A34A;
}

/* ================= ACTIONS COLUMN ================= */
.td-actions {
  white-space: nowrap;
  position: relative;
}

.act-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid #E2E8F0;
  background: #fff;
  color: #475569;
  transition: all 0.15s;
  font-family: 'Inter', sans-serif;
  white-space: nowrap;
  line-height: 1.4;
  letter-spacing: 0.2px;
}

.act-btn:hover {
  background: #F1F5F9;
  border-color: #CBD5E1;
  color: #1E293B;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

.act-edit {
  color: #5B9BD5;
  border-color: #rgba(91, 155, 213, 0.25);
}

.act-edit:hover {
  background: #5B9BD5;
  border-color: #5B9BD5;
  color: #fff;
  box-shadow: 0 1px 4px #rgba(91, 155, 213, 0.25);
}

.act-delete {
  background: #FEF2F2;
  border-color: rgba(220, 38, 38, 0.3);
  color: #DC2626;
}

.act-delete:hover {
  background: #FEE2E2;
  border-color: rgba(220, 38, 38, 0.5);
  color: #B91C1C;
  box-shadow: 0 1px 3px rgba(220, 38, 38, 0.12);
}

.act-delete:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

/* ================= PAGINATION ================= */
.ats-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 12px;
  padding: 8px 0;
}

.page-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 10px;
  border: 1px solid #E2E8F0;
  border-radius: 6px;
  background: #fff;
  color: #475569;
  font-size: 12px;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.12s;
}

.page-btn:hover:not(:disabled) {
  background: #F1F5F9;
  border-color: #CBD5E1;
}

.page-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  align-items: center;
  gap: 2px;
}

.page-num {
  width: 30px;
  height: 30px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: none;
  color: #64748B;
  font-size: 12px;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
  transition: all 0.12s;
}

.page-num:hover {
  background: #F1F5F9;
  border-color: #E2E8F0;
}

.page-num-active {
  background: #0F4C81 !important;
  color: #fff !important;
  font-weight: 800 !important;
  text-decoration: underline !important;
}

.page-num-active:hover {
  background: #0F4C81;
}

.page-info {
  font-size: 11px;
  color: #94A3B8;
  margin-left: 8px;
}

/* ================= EDIT MODAL ================= */
.job-edit-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 16px;
}

.job-edit-modal {
  width: 100%;
  max-width: 620px;
  background: #fff;
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  max-height: 90vh;
  overflow-y: auto;
}

.job-edit-modal h3 {
  margin-top: 0;
  margin-bottom: 16px;
  color: #0F172A;
}

.job-edit-modal label {
  display: block;
  margin-top: 10px;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #0F172A;
}

.job-edit-modal input,
.job-edit-modal textarea {
  width: 100%;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  box-sizing: border-box;
}

.job-edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 14px;
}

.save-job-btn {
  background: #5B9BD5;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  cursor: pointer;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
}

.cancel-job-btn {
  background: #e2e8f0;
  color: #0f172a;
  border: none;
  border-radius: 8px;
  padding: 10px 16px;
  cursor: pointer;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
}

/* ================= RESPONSIVE ================= */
@media (max-width: 900px) {
  .th-created, .td-created { display: none; }
  .th-jobdate, .td-jobdate { display: none; }
  .th-metrics, .td-metrics { width: auto; }
  .opening-title { max-width: 180px; }
  .metric-val { font-size: 13px; }
  .metric-remaining { font-size: 15px; }
}

@media (max-width: 600px) {
  .th-department, .td-department { display: none; }
  .th-metrics, .td-metrics { display: none; }
  .th-actions, .td-actions { width: auto; }
  .opening-title { max-width: 140px; }
  .ats-header { flex-direction: column; gap: 10px; }
  .ats-header-right { width: 100%; }
  .ats-primary-btn { width: 100%; text-align: center; }
  .ats-table td { padding: 10px 12px; }
  .jdb-search-input { width: 100%; }
  .jdb-select { min-width: 100px; }
}
`;
